import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, Grid, Typography, Button, Tabs, Tab, useTheme, TextField, Menu, MenuItem, InputAdornment, Tooltip, IconButton, CircularProgress } from "@mui/material";
import { ExpandMore, VisibilityOutlined, VisibilityOffOutlined, WarningAmber } from '@mui/icons-material';
import { Card, ModalSupport } from "../ui";
import { styled } from "../../theme";
import { AppHeader } from "../Login/components";
import { ReactComponent as RussiaIcon  } from '../../assets/icons/icon_ru_flag.svg'
import { ReactComponent as UzbekistanIcon  } from '../../assets/icons/icon_uz_flag.svg'
import { ReactComponent as AskChatIcon  } from '../../assets/icons/icon_ask_chat.svg'
import maskPhoneInput from '../../helpers/maskPhoneInput';
import formatPhoneNumber from '../../helpers/formatPhoneNumber';
import { sanitizePhone } from '../../helpers/sanitizePhone';
import { postChangePassword, postRecoveryCode, postValidateRecoveryCode } from "../../network/recovery";
import { showNotification } from "../../reducers/App";


const messageTemplates = {
  phone:    "Мы отправим вам код для подтверждения. Укажите зарегистрированный в системе номер телефона.",
  login:    "Мы отправим вам код для подтверждения. Укажите логин.",
  code:     "Введите код. Посмотреть код можно в мобильном приложении Диспетчер 24.",
  password: "Придумайте новый пароль."
};

function Recovery() {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()

  const [selectedTab, setSelectedTab] = useState('phone');
  const [step, setStep] = useState('input');
  const [showPassword, setShowPassword] = useState({
    password:             false,
    password_validate:    false
  });

  const [submitData, setSubmitData] = useState({ 
    login:              null,
    phone:              null,
    password:           null,
    password_validate:  null,
    salt:               null
  })

  const [codeData, setCodeData] = useState({ 
    code: null,
    sms_cooldown: 59
  })

  // Обработка состояния запроса
    const [loadingStatus, setLoadingStatus] = useState({
      isLoading:        false,
      error:            false
    });

  const [countrySelect, setCountrySelect] = useState({
    anchorEl: null,
    countryCode: 'RU',
    phoneCode: '+7',
    phoneLength: 10,
    placeholder: '+7 (_ _ _) _ _ _ - _ _ - _ _'
  })
  const openCountrySelect = Boolean(countrySelect.anchorEl);

  // Модальные окна
  const [modalSupport, setModalSupport] = useState(false);

  let submitPhone = sanitizePhone(submitData.phone, countrySelect.phoneCode, countrySelect.countryCode) 

  const handleTabChange = (newValue) => { 
    setSelectedTab(newValue)
    setSubmitData((prevData) => ({ ...prevData, login: null, phone: null, password: null, password_validate: null, salt: null }))
  }

  const handleCountrySelect = (countryCode) => {
    setSubmitData((prevData) => ({ ...prevData, login: null, phone: null, password: null, passwod_validate: null, salt: null }))
    setCountrySelect((prev) => ({ 
      ...prev, 
      anchorEl: null,
      countryCode: countryCode,
      phoneCode:   countryCode === 'UZ' ? '+998' : '+7',
      phoneLength: countryCode === 'UZ' ? 9 : 10,
      placeholder: countryCode === 'UZ' ? '+998 _ _ - _ _ _ - _ _ _ _' : '+7 (_ _ _) _ _ _ - _ _ - _ _'
    }))
  }

  useEffect(() => {
    let timer;
    
    if (step === 'code' && codeData.sms_cooldown > 0) {
      timer = setInterval(() => {
        setCodeData(prev => {
          if (prev.sms_cooldown <= 1) {
            clearInterval(timer);
            return { ...prev, sms_cooldown: 0 };
          }
          return { ...prev, sms_cooldown: prev.sms_cooldown - 1 };
        });
      }, 1000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [step, codeData.sms_cooldown])

  
  const sendCode = async(e, flag_send_sms = false) => {
    if (e) e.preventDefault();

    if(!loadingStatus.isLoading) {
      setLoadingStatus({ isLoading: true, error: null })

      let params = { flag_send_sms }

      selectedTab === 'phone' ? params.phone = submitPhone.trim() : params.login = submitData.login.trim()
  
      postRecoveryCode(params, (err, resp) => {
        let errorText = err?.errorMsg.replace('Error: ', '')

        setLoadingStatus({ isLoading: false, error: !err?.ok ? errorText : null })
        if(!err) setStep('code');
      })
    }
  }

  const validateCode = async(e) => {
    if (e) e.preventDefault();

    if(!loadingStatus.isLoading) {
      setLoadingStatus({ isLoading: true, error: null })

      let params = { code: codeData.code }

      selectedTab === 'phone' ? params.phone = submitPhone.trim() : params.login = submitData.login.trim()

      postValidateRecoveryCode(params, (err, resp) => {

        if(resp.salt) {
          setSubmitData((prevData) => ({ ...prevData, salt: resp.salt }))
        }
        setLoadingStatus({ isLoading: false, error: (!err?.ok && !resp.salt) ? 'Введен неверный код' : null })
        if(!err && resp.salt) setStep('password');
      })
    }
  }

  const savePassword = async(e) => {
    if (e) e.preventDefault();
    
    if(submitData.password !== submitData.password_validate) {
      setLoadingStatus(prev => ({ ...prev, error: 'Пароли не совпадают' }))

      return;
    }

    if(!loadingStatus.isLoading) {
      setLoadingStatus({ isLoading: true, error: null })

      let params = { code: codeData.code, salt: submitData.salt, password: submitData.password.trim() }
      selectedTab === 'phone' ? params.phone = submitPhone.trim() : params.login = submitData.login.trim()
      

      postChangePassword(params, (err, resp) => {
        let errorText = err?.errorMsg.replace('Error: ', '')

        setLoadingStatus({ isLoading: false, error: !err?.ok ? errorText : null })
        if(!err) {
          dispatch(showNotification({ message: 'Пароль успешно изменен!', severity: 'success' }))
          navigate('/')  
        }   
      })
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100%',
        bgcolor: '#EDEFF0'
      }}
    >
      <Card sx={{ display: 'flex', flexDirection: 'column', minWidth: { xs: 'auto', md: '38rem' }, p: { xs: 1.5, md: '2rem' } }}>
        <AppHeader title="Восстановление пароля" />

        {step === 'input' && (
          <Tabs onChange={(e, newValue) => handleTabChange(newValue)} value={selectedTab} sx={{...styled('tabs', 'sx'), my: 3.5 }}>
            <Tab value={"phone"} label='По телефону' />
            <Tab value={"login"} label='По логину' />
          </Tabs>
        )}

        <form onSubmit={(e) => {
          e.preventDefault();
          if (step === 'input') sendCode(e);
          if (step === 'code') validateCode(e);
          if (step === 'password') savePassword(e);
        }}>
        <Grid sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: step !== 'input' ? 3.5 : 0 }}>
            <Grid container gap={2}>
              <Typography sx={{ color: theme.palette.grey.main, fontSize: { xs: 14, md: 16 } }}>
                {step === 'input' 
                  ? messageTemplates[selectedTab] : step === 'code' ? messageTemplates.code : messageTemplates.password }
              </Typography>

              {step !== 'password' && (
                <Box>
                  <Typography sx={{ color: theme.palette.grey.main, fontSize: { xs: 14, md: 16 } }}>Если возникли трудности, обратитесь в</Typography>
                  <Typography onClick={() => setModalSupport(true)} sx={{ color: theme.palette.blue, cursor: 'pointer', fontSize: { xs: 14, md: 16 } }}>Службу поддержки</Typography>
                </Box>
              )}
            </Grid>

            {/* Шаг ввода данных */}
            {step === 'input' && (
              <Grid container gap={3}>
                <Grid item xs={12} >
                  {selectedTab === 'phone'? (
                    <Grid xs={12} item display='flex' direction='column' gap={0.5}>
                      <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Телефон *</Typography>
                      <Grid container spacing={1} sx={{ flexDirection: 'row' }}>
                        <Grid item xs={2}>
                          <TextField fullWidth size="small"      
                            onClick={(e) => setCountrySelect((prev) => ({ ...prev, anchorEl: e.currentTarget }))}
                            sx={{ ...styled('autocomplete',"input", "filled"), '& .MuiInputBase-input': { cursor: 'pointer' } }}      
                            disabled={loadingStatus.isLoading}   
                            InputProps={{
                              readOnly: true,
                              startAdornment: 
                                <Box sx={{ display: 'flex', alignItems: 'center', ml: { xs: 0.2, sm: 1.5 } }}>
                                  {countrySelect.countryCode == 'UZ' 
                                    ? <UzbekistanIcon sx={{ width: 24, height: 18 }} /> 
                                    : <RussiaIcon sx={{ width: 24, height: 18 }} />
                                  }
                                </Box>,
                              endAdornment: <ExpandMore sx={{ color: 'grey.500', mr: { xs: 0.2, sm: 1 } }} />,
                            }}
                          />
                          <Menu
                            open={openCountrySelect}
                            anchorEl={countrySelect.anchorEl}
                            onClose={() => setCountrySelect((prev) => ({ ...prev, anchorEl: null }))}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            disableAutoFocusItem
                            sx={{...styled('menu', 'sx'),  '& .MuiList-root': { gap: 1 }}}
                            slotProps={styled('menu', 'slotProps')}
                          >
                            <MenuItem selected={countrySelect.countryCode === 'RU'} onClick={() => handleCountrySelect('RU')}>
                              <Box sx={{ display: 'flex',  gap: 1 }}>
                                <RussiaIcon />
                                <Typography fontSize={14}>Россия</Typography>
                              </Box>
                            </MenuItem>
                            <MenuItem selected={countrySelect.countryCode === 'UZ'} onClick={() => handleCountrySelect('UZ')}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <UzbekistanIcon />
                                <Typography fontSize={14}>Узбекистан</Typography>
                              </Box>
                            </MenuItem>
                          </Menu>
                        </Grid>
                        <Grid item xs={10}>
                          <TextField fullWidth size="small" 
                            disabled={loadingStatus.isLoading}       
                            value={
                              (() => {
                                let phone = submitData.phone ?? '';
                                const phoneCode = countrySelect.phoneCode;
                                const countryCode = countrySelect.countryCode || 'RU';
                                const phoneLength = countrySelect.phoneLength;

                                return formatPhoneNumber(phone, phoneCode, countryCode, phoneLength);
                              })()
                            }
                            onChange={(e) => {
                              maskPhoneInput(
                                e.target.value,
                                countrySelect.phoneCode || '+7',
                                countrySelect.phoneLength || 10,
                                (formattedPhone) => {
                                  setSubmitData((prevData) => ({ ...prevData, phone: formattedPhone, login: null, password: null, password_validate: null, salt: null }));
                                }
                              );
                            }}
                            sx={{ ...styled('autocomplete',"input", "filled") }}         
                            placeholder={countrySelect.placeholder} 
                          />
                        </Grid>
                      </Grid>
                    </Grid>        
                  ) : (
                      <Grid xs={12} item display='flex' direction='column' gap={0.5} >
                      <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Логин *</Typography>
                      <TextField fullWidth size="small"      
                        value={submitData.login ?? ''}
                        disabled={loadingStatus.isLoading}  
                        onChange={(e) => { setSubmitData((prevData) => ({ ...prevData, login: e.target.value, phone: null, password: null, password_validate: null, salt: null }))}}
                        sx={{ ...styled('autocomplete',"input", "filled") }}         
                        placeholder="Введите логин" 
                      />
                    </Grid>
                  )}
                </Grid>
              </Grid>
            )}

            {/* Шаг ввода кода */}
            {step === 'code' && (
              <Grid container gap={2}>
                <Grid xs={12} item display='flex' direction='column' gap={0.5}>
                  <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Код *</Typography>
                  <TextField fullWidth size="small"      
                    value={codeData.code ?? ''}
                    disabled={loadingStatus.isLoading}  
                    onChange={(e) => setCodeData((prevData) => ({ ...prevData, code: e.target.value }))}
                    sx={{ ...styled('autocomplete',"input", "filled") }}         
                    placeholder="Введите код" 
                  />
                </Grid>

                {codeData.sms_cooldown > 0
                  ? <Typography sx={{ color: theme.palette.grey.main, fontSize: { xs: 14, md: 16 } }}>Отправить код по СМС через {codeData.sms_cooldown} сек  </Typography>
                  : <Typography 
                       
                      onClick={(e) => { !loadingStatus.isLoading && setCodeData((prev) => ({ ...prev, sms_cooldown: 59 })); sendCode(e, true) }}
                      sx={{ color: step === 'code' ? theme.palette.blue : theme.palette.grey.main, cursor: 'pointer', fontSize: { xs: 14, md: 16 } }}
                    >
                      Отправить код по СМС
                    </Typography>
                }
              </Grid>
            )}

            {/* Шаг ввода нового пароля */}
            {step === 'password' && (
              <Grid container gap={2}>
                <Grid xs={12} item display='flex' direction='column' gap={0.5}>
                  <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Новый пароль *</Typography>
                  <TextField fullWidth size="small"    
                    disabled={loadingStatus.isLoading}    
                    value={submitData.password ?? ''}
                    type={showPassword.password ? 'text' : 'password'}   
                    onChange={(e) => setSubmitData((prevData) => ({ ...prevData, password: e.target.value  }))}
                    sx={{ ...styled('autocomplete',"input", "filled") }}         
                    placeholder="Введите пароль" 
                    InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: 1 }}>
                        <Tooltip placement='top' title={showPassword.password ? 'Скрыть пароль' : 'Показать пароль'}>
                          <IconButton
                            sx={{ opacity: submitData.password ? 1 : 0, transition: 'opacity 0.3s ease', pointerEvents: submitData.password ? 'auto' : 'none' }}
                            onClick={() => setShowPassword((prev) => ({ ...prev, password: !prev.password }))}
                            edge="end"
                            size="small"
                          >
                            {showPassword.password ? <VisibilityOffOutlined sx={{ fill: theme.palette.grey.main }} /> : <VisibilityOutlined sx={{ fill: theme.palette.grey.main }} />}
                          </IconButton>
                        </Tooltip>
                        </InputAdornment>  
                      ),
                    }}
                  />
                </Grid>

                <Grid xs={12} item display='flex' direction='column' gap={0.5}>
                  <Typography fontSize={14} sx={{ color: theme.palette.grey.main }}>Повторите новый пароль *</Typography>
                  <TextField fullWidth size="small"  
                  type={showPassword.password_validate ? 'text' : 'password'}       
                    value={submitData.password_validate ?? ''}
                    disabled={loadingStatus.isLoading}  
                    onChange={(e) => setSubmitData((prevData) => ({ ...prevData, password_validate: e.target.value  }))}
                    sx={{ ...styled('autocomplete',"input", "filled") }}         
                    placeholder="Введите пароль" 
                    InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: 1 }}>
                        <Tooltip placement='top' title={showPassword.password_validate ? 'Скрыть пароль' : 'Показать пароль'}>
                          <IconButton
                            sx={{ opacity: submitData.password_validate ? 1 : 0, transition: 'opacity 0.3s ease', pointerEvents: submitData.password_validate ? 'auto' : 'none' }}
                            onClick={() => setShowPassword((prev) => ({ ...prev, password_validate: !prev.password_validate }))}
                            edge="end"
                            size="small"
                          >
                            {showPassword.password_validate ? <VisibilityOffOutlined sx={{ fill: theme.palette.grey.main }} /> : <VisibilityOutlined sx={{ fill: theme.palette.grey.main }} />}
                          </IconButton>
                        </Tooltip>
                        </InputAdornment>  
                      ),
                    }}
                  />
                </Grid>

              </Grid>
            )}

            {loadingStatus.error && (
              <Grid sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <WarningAmber sx={{ fill: theme.palette.error.main, cursor: 'auto' }} />
                <Typography sx={{ color: theme.palette.error.main, fontSize: 14 }}>{loadingStatus.error || 'Произошла ошибка'}</Typography>
              </Grid>
            )}

            <Grid sx={{ display: 'flex', gap: 1.5, pt: 1 }}>
              {step === 'input' && (
                <Button 
                  // onClick={() => sendCode()} 
                  sx={{ ...styled('button',"secondary") }} 
                  disableElevation 
                  type="submit"
                  variant="contained" 
                  color={"green"}
                  disabled={
                    (selectedTab === 'phone' && (!submitData.phone || !submitData.phone.trim())) ||
                    (selectedTab === 'login' && (!submitData.login || !submitData.login.trim())) ||
                    loadingStatus.isLoading
                  }
                >
                  {loadingStatus.isLoading && (<Box  sx={{ alignItems: 'center', display: 'flex' }} pr={1}><CircularProgress size={15} color='inherit' /></Box>)}
                  Получить код
                </Button>
              )}
              {step === 'code' && (
                <Button type="submit" disabled={(!codeData.code || (!codeData.code.trim())) || loadingStatus.isLoading} sx={{ ...styled('button',"secondary") }} disableElevation variant="contained" color={"green"}>
                  {loadingStatus.isLoading && (<Box  sx={{ alignItems: 'center', display: 'flex' }} pr={1}><CircularProgress size={15} color='inherit' /></Box>)}
                  Подтвердить
                </Button>
              )}
              {step === 'password' && (
                <Button 
                  type="submit"
                  // onClick={() => savePassword()} 
                  disabled={((!submitData.password || !submitData.password.trim()) || (!submitData.password_validate || !submitData.password_validate.trim())) || loadingStatus.isLoading} 
                  sx={{ ...styled('button',"secondary") }} disableElevation variant="contained" color={"green"}
                >
                  {loadingStatus.isLoading && (<Box  sx={{ alignItems: 'center', display: 'flex' }} pr={1}><CircularProgress size={15} color='inherit' /></Box>)}
                  Сохранить
                </Button>
              )}
            <Button disabled={loadingStatus.isLoading} onClick={() => navigate('/')} sx={{ ...styled('button',"secondary") }} color='grey' variant="outlined">На страницу входа</Button>
          </Grid>

          {step === 'password' && (
            <Grid sx={{ display: 'flex', gap: 1.5, alignItems: 'center', pt: 1 }}>
            <AskChatIcon style={{ cursor: 'default' }} />
              <Box sx={{ display: 'flex', gap: 1 }}>
                  <Typography sx={{ color: theme.palette.grey.main, fontSize: { xs: 14, md: 16 } }}>Возникли проблемы?</Typography>
                  <Typography onClick={() => setModalSupport(true)} sx={{ color: theme.palette.blue, cursor: 'pointer', fontSize: { xs: 14, md: 16 } }}>Службу поддержки</Typography>
                </Box>
            </Grid>
          )}
         </Grid> 

        <ModalSupport open={modalSupport} onClose={() => setModalSupport(false)} />
      </form>
      </Card>
    </Box>
  )
}

export default Recovery;